import React, { useState, useRef, useCallback, useMemo } from 'react';

import { functionNoop } from 'src/utils/function/noop';
import {
  UploadRegularIcon,
  PhotoRegularIcon,
  ChevronRightRegularIcon,
  StepRegularIcon,
  FrameImageRegularIcon,
  RefreshLightIcon,
} from 'src/monet/icons';
import { JourneyMenu } from 'src/common/JourneyMenu';
import { Nullable } from 'src/types/nullable.type';

import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { ShareThumbnailPickFromStepPanel } from './pick-from-step-panel';
import { useCurrentOrganization } from 'src/store/organization';
import { useSharePanelStore } from 'src/common/store/share.store';
import { Button } from 'src/common/button';
import { ShareLinkFormType } from '../../link-config-form.factory';
import { useFormikContext } from 'formik';

export type DashboardHomeActionPanelTypes = 'pick-from-step';
const MUX_IMAGE_HOST = 'https://image.mux.com';

interface Props {
  onOpen: () => void;
  onClose: () => void;
  isNeueActive: boolean;
  onUploadClicked: () => void;
  nestedMenuPlacement: JourneyMenuPropsInterface['placement'];
}

const menuItemClasses = 'text-bedrock-p text-bedrock-black flex items-center space-x-sm';

export const ShareThumbnailOptions = ({
  onOpen,
  onClose,
  onUploadClicked,
  nestedMenuPlacement,
  isNeueActive,
}: Props) => {
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const { setSelectedThumbnailSourceStep, setShowThumbnailSlider, setSelectedImageUrl, journey, encodedMuxAsset } =
    useSharePanelStore((state) => ({
      setSelectedThumbnailSourceStep: state.setSelectedThumbnailSourceStep,
      setShowThumbnailSlider: state.setShowThumbnailSlider,
      setSelectedImageUrl: state.setSelectedImageUrl,
      journey: state.sharedJourney,
      encodedMuxAsset: state.encodedMuxAsset,
    }));
  const [activePanel, setActivePanel] = useState<Nullable<DashboardHomeActionPanelTypes>>(null);
  const thumbnailUrl = null;

  const {
    setFieldValue,
    values: { personalization_logo_url: personalizationLogoUrl },
  } = useFormikContext<ShareLinkFormType>();

  const openRef = useRef(functionNoop);
  const closeRef = useRef(functionNoop);

  const pickFromStepOpenRef = useRef(functionNoop);
  const pickFromStepCloseRef = useRef(functionNoop);

  const canPickFromVideo = Boolean(encodedMuxAsset);
  const onMenuClicked = () => {
    openRef.current();
  };

  const onBack = () => {
    setActivePanel(null);
    pickFromStepCloseRef.current();
    setTimeout(onMenuClicked, 50);
  };

  const onClosePanel = useCallback(() => {
    setActivePanel(null);
    onClose();
  }, [onClose]);

  const onShowThumbnailSlider = useCallback(() => {
    setShowThumbnailSlider(true);
    setSelectedImageUrl(thumbnailUrl);
  }, [setSelectedImageUrl, setShowThumbnailSlider, thumbnailUrl]);

  const onPickFrameFromVideoClick = () => {
    onShowThumbnailSlider();
    onClosePanel();
  };
  const animatedImageUrl = useMemo(() => {
    if (!encodedMuxAsset) return null;

    return `${MUX_IMAGE_HOST}/${encodedMuxAsset?.public_playback_id}/animated.gif`;
  }, [encodedMuxAsset]);

  const onGenerateAnimatedGifFromVideoClick = useCallback(() => {
    setSelectedImageUrl(animatedImageUrl);
  }, [animatedImageUrl, setSelectedImageUrl]);

  const onPickFromStepClick = useCallback(
    (step) => {
      const selectedThumbnailUrl = step.thumbnail ? step.thumbnail.url : step.external_link?.meta_image_url;
      if (journey?.thumbnail_url !== selectedThumbnailUrl) {
        setSelectedImageUrl(selectedThumbnailUrl);
        if (step?.video_asset?.mux_asset && step?.video_asset?.mux_asset?.status === 'ready') {
          setSelectedThumbnailSourceStep(step);
          setShowThumbnailSlider(true);
        }
      }
      onClosePanel();
    },
    [journey?.thumbnail_url, onClosePanel, setSelectedImageUrl, setSelectedThumbnailSourceStep, setShowThumbnailSlider]
  );

  const onPickFromAnotherStepClick = () => {
    setActivePanel('pick-from-step');
  };

  const onUseBrandLogoClick = () => {
    const { logo_url, journey_thumbnail_url } = currentOrganization.branding;
    const brandingLogoUrl = logo_url || journey_thumbnail_url;
    setSelectedImageUrl(brandingLogoUrl);
  };

  const renderThumbnailMenuButton = () => (
    <Button size='regular' variant='secondary' label='Change thumbnail' onClick={onMenuClicked} />
  );

  if (activePanel === 'pick-from-step') {
    return (
      <ShareThumbnailPickFromStepPanel
        onOpen={onOpen}
        onClose={onClosePanel}
        openRef={pickFromStepOpenRef}
        closeRef={pickFromStepCloseRef}
        onBack={onBack}
        placement={nestedMenuPlacement || 'bottom-start'}
        popperClassName='!fixed'
        onClickItem={onPickFromStepClick}
        renderOrigin={renderThumbnailMenuButton}
      />
    );
  }

  return (
    <JourneyMenu
      placement={nestedMenuPlacement || 'bottom-start'}
      popperClassName='!fixed'
      openRef={openRef}
      closeRef={closeRef}
      onOpen={onOpen}
      onClose={onClose}
      renderOrigin={renderThumbnailMenuButton}
    >
      {canPickFromVideo && (
        <>
          <JourneyMenu.Item className={menuItemClasses} onClick={onPickFrameFromVideoClick}>
            <FrameImageRegularIcon />
            <span>Pick from frame of video</span>
          </JourneyMenu.Item>
          <JourneyMenu.Item className={menuItemClasses} onClick={onGenerateAnimatedGifFromVideoClick}>
            <PhotoRegularIcon />
            <span>Pick animated thumbnail</span>
          </JourneyMenu.Item>
        </>
      )}
      {!isNeueActive && (
        <JourneyMenu.Item className={menuItemClasses} onClick={onPickFromAnotherStepClick}>
          <StepRegularIcon />
          <span>Pick from another Step</span>
          <span className='!ml-auto'>
            <ChevronRightRegularIcon />
          </span>
        </JourneyMenu.Item>
      )}
      <JourneyMenu.Item className={menuItemClasses} onClick={onUploadClicked}>
        <UploadRegularIcon />
        <span>Upload an image</span>
      </JourneyMenu.Item>
      {/* <JourneyMenu.Item className={menuItemClasses} onClick={onUploadClicked}>
        <CameraRegularIcon />
        <span>Take a photo</span>
      </JourneyMenu.Item> */}
      <JourneyMenu.Item className={menuItemClasses} onClick={onUseBrandLogoClick}>
        <RefreshLightIcon />
        <span>Use default thumbnail</span>
      </JourneyMenu.Item>
    </JourneyMenu>
  );
};
